import { Col, DatePicker, Flex, Radio, Row, Typography } from "antd";
import CustomModal from "../Modals/CustomModal";
import React, { useEffect, useMemo, useState } from "react";
import Alert from "./Alert";
import { CloseOutlined } from "@ant-design/icons";
import CustomButton from "../CustomButton";
import ShivversAlertDTO from "src/models/ShivversAlertDTO";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import AlertDTO from "src/models/AlertDTO";
import AlertCategory from "src/consts/AlertCategory";
import dayjs, { Dayjs } from "dayjs";
import { useGetPremierPlusAlertsQuery } from "src/queries/useGetPremierPlusAlertsQuery";

const { Title } = Typography; 

interface AlertsModalProps{
    binId: number | null;
    showAlertsModal: boolean;
    onClose: () => void;
    binDto: PremierPlusBinDTO | null | undefined;
    parentRef?: React.RefObject<HTMLElement>;
}

const { Text } = Typography;

interface PaginatedAlertsProps {
    alerts: AlertDTO[];
}

const PaginatedAlerts: React.FC<PaginatedAlertsProps> = ({ alerts }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 3;

    const totalPages = Math.max(1, Math.ceil(alerts.length / pageSize));

    const paginatedAlerts = useMemo(() => {
        const startIdx = (currentPage - 1) * pageSize;
        const endIdx = startIdx + pageSize;
        const result = alerts.filter(a => a.lastTriggerTime != null).slice(startIdx, endIdx);
        return result;
    }, [alerts, currentPage]);

    const handleNext = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    };

    const handlePrevious = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    if(alerts.length === 0){
        return (
            <Col>
                <Text>No alerts found</Text>
            </Col>
        )
    }

    return (
        <Col>
            <Row style={{ marginBottom: 8 }}>
                <Text>{currentPage}/{totalPages}</Text>
            </Row>

            <Row gutter={[8, 8]}>
                {paginatedAlerts.map((a, idx) => (
                <Col key={`${a.alertTypeName}-${idx}`} span={24}>
                    <Alert key={`${a.alertCategoryId}-${a.alertTypeName}-${a.alertTypeId}-${idx}`}date={a.lastTriggerTime} title={a.title} description={a.message} />
                </Col>
                ))}
            </Row>

            <Row justify="space-between" style={{ marginTop: 16 }}>
                <Col>
                    {currentPage > 1 && (<CustomButton variant="primary" label="Previous" onClick={handlePrevious} maxWidth={100}/>)}
                </Col>
                <Col>
                    {currentPage < totalPages && (<CustomButton variant="primary" label="Next" onClick={handleNext} maxWidth={100}/>)}
                </Col>
            </Row>
        </Col>
    );
};

const { RangePicker } = DatePicker;

const AlertsModal : React.FC<AlertsModalProps> = ({ parentRef, binId, showAlertsModal, onClose, binDto }) => {

    const [showActiveAlerts, setShowActiveAlerts] = useState(true);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([dayjs().subtract(2, "week"), dayjs()]);
    const { getPremierPlusAlertsQuery } = useGetPremierPlusAlertsQuery(binId, dateRange[0]?.toISOString(), dateRange[1]?.toISOString(), { enabled: true, refetchOnWindowFocus: false });

    const activeAlerts = useMemo ( () => {
        return binDto?.activeAlerts ?? [];
    }, [binDto]);

    const pastAlerts = useMemo( () => {
        return getPremierPlusAlertsQuery.data ?? [];
    }, [getPremierPlusAlertsQuery.data]);

    const onDateChange = (vals: [Dayjs | null, Dayjs | null]) => {
        setDateRange(vals);
    };

    return(            
        <CustomModal 
        show={showAlertsModal} 
        onClose={onClose}       
        title={'Alerts'}
        parentRef={parentRef}
        >
            <Radio.Group
            buttonStyle={"solid"} 
            block 
            optionType="button"
            value={showActiveAlerts}
            onChange={(val) => setShowActiveAlerts(val.target.value)}
            >
                <Radio.Button value={true}>Active</Radio.Button>
                <Radio.Button value={false}>Previous</Radio.Button>
            </Radio.Group>
            <Flex justify='center' vertical style={{ maxWidth:'800px', maxHeight:'800px', paddingTop: '8px', paddingBottom: '8px', overflow:'auto'}}>
                <Row>
                    <Col>
                        <Row justify={'space-between'} align='middle' style={{ width: '100%' }}>
                            <Col>
                                <Title level={4}>{showActiveAlerts ? 'Active Alerts' : 'Previous Alerts'}</Title>
                            </Col>
                            {
                                !showActiveAlerts && 
                                <Col>
                                    <RangePicker
                                    value={dateRange}
                                    onChange={(vals) => onDateChange(vals as [Dayjs | null, Dayjs | null])}
                                    allowClear
                                    />
                                </Col>
                            }
                        </Row> 
                        <Row>
                            {
                                showActiveAlerts ? 
                                <PaginatedAlerts alerts={activeAlerts} />
                                :
                                <PaginatedAlerts alerts={pastAlerts}/>
                            }
                        </Row>
                    </Col>
                </Row>
                <br/>
            </Flex>
            <Row justify={'end'}>
                <CustomButton variant='secondary' label={'Close'} icon={<CloseOutlined/>} onClick={onClose}/>
            </Row>
        </CustomModal>
    )
}

export default AlertsModal;